import { useMemo, useState } from 'react';
import { Button, Drawer, Radio, Select, Space, Typography, message } from 'antd';
import {
	TrackedCard,
	useInitiateMoveMutation,
	useConfirmMoveMutation,
	useUndoMoveMutation,
	useListStagingAreasQuery,
	useListTrackedCardsQuery,
} from '../../redux/api/kit-staging';
import { Area, Destination, lookup, legalDestinations } from './moveRulesClient';
import { showMoveError, showMoveSuccess } from './moveMessages';

interface Props {
	open: boolean;
	card: TrackedCard;
	onClose: () => void;
}

export function MoveDrawer({ open, card, onClose }: Props) {
	const [target, setTarget] = useState<Destination | null>(null);
	const [shelfId, setShelfId] = useState<number | null>(null);
	const [reason, setReason] = useState<string>('manually_removed');
	const [initiate, { isLoading: initiating }] = useInitiateMoveMutation();
	const [confirm, { isLoading: confirming }] = useConfirmMoveMutation();
	const [undo, { isLoading: undoing }] = useUndoMoveMutation();
	const { data: stagingAreas } = useListStagingAreasQuery(undefined, { pollingInterval: 10000 });
	const { data: allCards } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });

	const shelfOptions = useMemo(() => {
		if (target !== 'msa' && target !== 'jsa') return [];
		const areaType = target === 'jsa' ? 'job_staging' : 'material_staging';
		const areas = (stagingAreas ?? []).filter((a) => a.area_type === areaType && a.is_active);
		// For JSA, only areas serving this job's machine group.
		const mgName = card.job?.machine_group?.name;
		const relevantAreas =
			target === 'jsa' && mgName
				? areas.filter((a) => a.machine_groups.some((mg) => mg.name === mgName))
				: areas;
		// JSA shelves are unique per (shelf, item_type). Mark occupied based on
		// active cards of the same item_type currently on the shelf.
		const occupied = new Set<number>();
		(allCards ?? []).forEach((c) => {
			if (
				c.current_area === target &&
				c.current_shelf != null &&
				c.id !== card.id &&
				(target === 'msa' || c.item_type === card.item_type)
			) {
				occupied.add(c.current_shelf);
			}
		});
		const opts: { value: number; label: string; disabled?: boolean }[] = [];
		for (const a of relevantAreas) {
			for (const r of a.racks) {
				for (const s of r.shelves) {
					if (!s.is_active) continue;
					const taken = occupied.has(s.id) || s.is_occupied;
					opts.push({
						value: s.id,
						label: `${a.name} · ${r.label} · #${s.position}${taken ? ' (occupied)' : ''}`,
						disabled: taken,
					});
				}
			}
		}
		return opts;
	}, [target, stagingAreas, allCards, card.id, card.item_type, card.job?.machine_group?.name]);

	const from = card.current_area as Area;
	const isReady = (d: Destination) => {
		if (d === 'jsa') return !!card.job?.kit_ready;
		if (d === 'msa') return !!(card.job?.material_ready || card.job?.material_partial);
		if (d === 'cart') return !!(card.job?.kit_ready || card.job?.material_ready || card.job?.material_partial);
		if (d === 'machine') return !!card.job?.kit_ready;
		return true;
	};
	const destinations = legalDestinations(from).filter(isReady);

	const submit = async (mode: 'single' | 'initiate') => {
		if (!target) return;
		const body: {
			id: number;
			target_area: Destination;
			target_shelf?: number;
			reason?: string;
		} = { id: card.id, target_area: target };
		if (target === 'msa' || target === 'jsa') {
			if (!shelfId) {
				message.warning('Pick a shelf before continuing.');
				return;
			}
			body.target_shelf = shelfId;
		}
		if (target === 'dekit') {
			body.reason = reason;
		}
		try {
			if (mode === 'single') {
				await confirm(body).unwrap();
				showMoveSuccess(`Moved to ${String(target).toUpperCase()}.`);
			} else {
				await initiate(body).unwrap();
				showMoveSuccess(`Pending move to ${String(target).toUpperCase()} — tap CONFIRM to finalize.`);
			}
			onClose();
		} catch (e) {
			showMoveError(e, `Couldn't move to ${String(target).toUpperCase()}.`);
		}
	};

	const handleUndo = async () => {
		try {
			await undo({ id: card.id }).unwrap();
			showMoveSuccess('Pending move undone.');
			onClose();
		} catch (e) {
			showMoveError(e, "Couldn't undo the pending move.");
		}
	};

	const rule = target ? lookup(from, target) : null;
	const isDual = rule === 'DUAL' || rule === 'DUAL_LOCAL';
	const isPending = card.pending_target_area != null;

	return (
		<Drawer open={open} onClose={onClose} title={`Move OLI #${card.order_line_item_id}`} width={420}>
			{isPending && (
				<Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
					<Typography.Text>
						Pending move to <b>{String(card.pending_target_area)}</b>
					</Typography.Text>
					<Button danger loading={undoing} onClick={handleUndo}>
						Undo pending move
					</Button>
				</Space>
			)}
			<Typography.Title level={5}>Destination</Typography.Title>
			<Radio.Group value={target} onChange={(e) => setTarget(e.target.value)}>
				{destinations.map((d) => (
					<Radio.Button key={d} value={d}>
						{d.toUpperCase()}
					</Radio.Button>
				))}
			</Radio.Group>
			{(target === 'msa' || target === 'jsa') && (
				<div style={{ marginTop: 12 }}>
					<Typography.Text strong>Shelf</Typography.Text>
					<Select
						value={shelfId ?? undefined}
						onChange={(v) => setShelfId(v ?? null)}
						placeholder={shelfOptions.length === 0 ? 'No shelves available' : 'Pick a shelf'}
						options={shelfOptions}
						style={{ width: '100%', marginTop: 4 }}
						allowClear
						showSearch
						optionFilterProp="label"
						disabled={shelfOptions.length === 0}
					/>
					{shelfOptions.length === 0 && (
						<Typography.Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
							No active {target.toUpperCase()} shelves match this card's machine group.
						</Typography.Text>
					)}
				</div>
			)}
			{target === 'dekit' && (
				<div style={{ marginTop: 12 }}>
					<Typography.Text>Reason</Typography.Text>
					<select aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} style={{ marginLeft: 8 }}>
						<option value="manually_removed">Manually Removed</option>
						<option value="site_mismatch">Site Mismatch</option>
						<option value="task_status_mismatch">Task Status Mismatch</option>
					</select>
				</div>
			)}
			<div style={{ marginTop: 24 }}>
				{target &&
					!isPending &&
					(rule === 'SINGLE' || rule === 'SINGLE_ERP' ? (
						<Button type="primary" loading={confirming} onClick={() => submit('single')}>
							Confirm move
						</Button>
					) : isDual ? (
						<Button type="primary" loading={initiating} onClick={() => submit('initiate')}>
							Initiate move
						</Button>
					) : null)}
			</div>
		</Drawer>
	);
}
